import React from 'react';
import { Container, Typography, Grid, Card, CardContent, Box, Divider, Avatar, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';

// Import placeholder images - replace these with actual photos
// You'll need to create an "images" folder in your assets directory
import sudhakarImg from '../assets/sudhakar2.jpeg';
import sailajaImg from '../assets/sailaja.jpeg';

// Styled components for enhanced visuals
const ProfileAvatar = styled(Avatar)(({ theme }) => ({
    width: 160,
    height: 160,
    border: `4px solid ${theme.palette.background.paper}`,
    boxShadow: theme.shadows[3],
    margin: '0 auto 1.5rem auto',
}));

const StyledCard = styled(Card)(({ theme }) => ({
    height: '100%',
    overflow: 'visible',
    position: 'relative',
    borderRadius: 16,
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    boxShadow: theme.shadows[3],
    '&:hover': {
        transform: 'translateY(-10px)',
        boxShadow: theme.shadows[10],
    },
}));

const PromoterCardContent = styled(CardContent)(({ theme }) => ({
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
}));

const HeaderDivider = styled(Divider)(({ theme }) => ({
    width: 120,
    height: 5,
    margin: '0 auto 2rem auto',
    backgroundColor: '#BF0A30',
    borderRadius: 4,
}));
const promoters = [
    {
        name: 'Mr. Sudhakar Reddy Gurijala',
        position: 'Founder & Managing Director',
        company: 'Shilpaa Electrical Infra Tech (India) Limited',
        image: sudhakarImg,
        bio: `Mr. Sudhakar Reddy Gurijala is the Promoter and Managing Director of Shilpaa Electrical Infra Tech (India) Limited. With over 25 years of industry experience, he has played a pivotal role in establishing the company as a trusted name in high-voltage electrical infrastructure solutions across India.
  
  He began his professional journey with the Andhra Pradesh Electricity Board as a Sub-Engineer (1993–1997), gaining critical field experience in power distribution. In 1997, he launched his entrepreneurial venture, laying the foundation for what would become Shilpaa Electrical Infra Tech. The company was formally incorporated as a private limited entity in 2007 to support its growing operations.
  
  Under his leadership, the company has earned an A-grade electrical contractor license and expanded its presence across multiple states, known for its focus on quality, safety, and innovation.`,
        education: 'Bachelor of Arts (B.A.)',
        highlights: [
            'Founded the company and led its transition to a private limited structure',
            'Secured A-grade electrical contractor status',
            'Expanded operations pan-India with a focus on high-voltage infrastructure',
            'Established a reputation for reliability, client satisfaction, and excellence'
        ],
        vision: 'Mr. Gurijala\'s vision continues to guide the organization\'s growth, with a mission to drive technological advancement and support India\'s infrastructure development.'
    },
    {
        name: 'Mrs. Sailaja Gurijala',
        position: 'Co-Promoter & Director',
        company: 'Shilpaa Electrical Infra Tech (India) Limited',
        image: sailajaImg,
        bio: `Mrs. Sailaja Gurijala serves as Promoter and Director at Shilpaa Electrical Infra Tech (India) Limited. With a sharp focus on operational efficiency and project execution, she plays a pivotal role in ensuring the smooth delivery of high-voltage electrical infrastructure solutions across multiple states in India.
  
  Since the company's transition from a sole proprietorship in 1997 to a private limited entity in 2007, Mrs. Gurijala has been instrumental in establishing the systems and processes that underpin its growth. Her leadership in operations has enabled the company to scale seamlessly, while maintaining its core values of precision, reliability, and client satisfaction.`,
        highlights: [
            'Leads operations with a focus on timely execution and inter-regional coordination',
            'Developed scalable systems to support multi-state project expansion',
            'Promotes a culture of quality, accountability, and process-driven delivery',
            'Played a central role in positioning the company as an A-grade electrical contractor'
        ],
        contributions: [
            'Laid the foundation for operational excellence and process optimization',
            'Supported the company\'s evolution into a respected player in high-voltage infrastructure',
            'Ensured consistent project outcomes aligned with client expectations and industry standards'
        ],
        vision: 'Mrs. Sailaja Gurijala continues to drive operational excellence at Shilpaa Electrical Infra Tech, aligning execution with the company\'s mission to deliver quality, safety, and innovation in every project.'
    },
];

export default function Promoters() {
    return (
        <Box sx={{
            py: 8,
            background: 'linear-gradient(to bottom, #f5f7fa, #ffffff)'
        }}>
            <Container maxWidth="lg">
                {/* Header Section with enhanced styling */}
                <Paper
                    elevation={0}
                    sx={{
                        mb: 8,
                        textAlign: 'center',
                        py: 4,
                        px: 2,
                        backgroundColor: 'transparent',
                        borderRadius: 4
                    }}
                >
                    <Typography
                        variant="h3"
                        component="h1"
                        gutterBottom
                        sx={{
                            fontWeight: 700,
                            color: '#BF0A30',
                            textTransform: 'uppercase',
                            letterSpacing: 1.2,
                            mb: 2
                        }}
                    >
                        Our Promoters
                    </Typography>
                    <HeaderDivider />
                    <Typography
                        variant="h6"
                        color="text.secondary"
                        sx={{
                            maxWidth: 800,
                            mx: 'auto',
                            lineHeight: 1.6,
                            fontWeight: 400
                        }}
                    >
                        Meet the founding visionaries who established Shilpaa Electrical Infra Tech and continue to drive its growth and success in the electrical infrastructure industry.
                    </Typography>
                </Paper>

                {/* Promoters Grid with enhanced styling */}
                <Grid container spacing={6} justifyContent="center">
                    {promoters.map((promoter, index) => (
                        <Grid item xs={12} md={6} key={index}>
                            <StyledCard>
                                <PromoterCardContent>
                                    {/* Profile Image */}
                                    <ProfileAvatar
                                        src={promoter.image}
                                        alt={promoter.name}
                                    />

                                    <Typography
                                        variant="h4"
                                        component="h2"
                                        sx={{
                                            fontWeight: 700,
                                            color: '#BF0A30',
                                            mb: 1
                                        }}
                                    >
                                        {promoter.name}
                                    </Typography>

                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            color: 'text.secondary',
                                            fontWeight: 500,
                                            mb: 0.5
                                        }}
                                    >
                                        {promoter.position}
                                    </Typography>

                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            color: 'text.secondary',
                                            fontStyle: 'italic',
                                            mb: 2
                                        }}
                                    >
                                        {promoter.company}
                                    </Typography>

                                    <Divider
                                        sx={{
                                            width: 60,
                                            height: 3,
                                            my: 2,
                                            backgroundColor: '#BF0A30',
                                            borderRadius: 2
                                        }}
                                    />

                                    {/* Bio Section */}
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            mt: 2,
                                            color: 'text.primary',
                                            textAlign: 'left',
                                            lineHeight: 1.8
                                        }}
                                    >
                                        {promoter.bio}
                                    </Typography>

                                    {/* Education Section - Only for Mr. Sudhakar */}
                                    {promoter.education && (
                                        <Box sx={{ mt: 3, width: '100%' }}>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    color: '#BF0A30',
                                                    fontWeight: 600,
                                                    mb: 1.5,
                                                    textAlign: 'left'
                                                }}
                                            >
                                                Educational Background:
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    textAlign: 'left',
                                                    mb: 2
                                                }}
                                            >
                                                {promoter.education}
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Key Highlights Section */}
                                    {promoter.highlights && (
                                        <Box sx={{ mt: 2, width: '100%' }}>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    color: '#BF0A30',
                                                    fontWeight: 600,
                                                    mb: 1.5,
                                                    textAlign: 'left'
                                                }}
                                            >
                                                Key Highlights:
                                            </Typography>
                                            <Box component="ul" sx={{ pl: 2, textAlign: 'left' }}>
                                                {promoter.highlights.map((highlight, idx) => (
                                                    <Typography
                                                        component="li"
                                                        variant="body1"
                                                        key={idx}
                                                        sx={{ mb: 1 }}
                                                    >
                                                        {highlight}
                                                    </Typography>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}

                                    {/* Contributions to Growth - Only for Mrs. Sailaja */}
                                    {promoter.contributions && (
                                        <Box sx={{ mt: 3, width: '100%' }}>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    color: '#BF0A30',
                                                    fontWeight: 600,
                                                    mb: 1.5,
                                                    textAlign: 'left'
                                                }}
                                            >
                                                Contributions to Growth:
                                            </Typography>
                                            <Box component="ul" sx={{ pl: 2, textAlign: 'left' }}>
                                                {promoter.contributions.map((contribution, idx) => (
                                                    <Typography
                                                        component="li"
                                                        variant="body1"
                                                        key={idx}
                                                        sx={{ mb: 1 }}
                                                    >
                                                        {contribution}
                                                    </Typography>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}

                                    {/* Vision Statement */}
                                    {promoter.vision && (
                                        <Box
                                            sx={{
                                                mt: 3,
                                                p: 2.5,
                                                bgcolor: '#f8f9fa',
                                                borderRadius: 2,
                                                borderLeft: '4px solid #BF0A30',
                                                width: '100%'
                                            }}
                                        >
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    fontStyle: 'italic',
                                                    textAlign: 'left',
                                                    lineHeight: 1.6
                                                }}
                                            >
                                                {promoter.vision}
                                            </Typography>
                                        </Box>
                                    )}
                                </PromoterCardContent>
                            </StyledCard>
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </Box>
    );
}